import {useCallback, useEffect, useState} from "react";

interface UseSearchBarOptions<T> {
    debounceMs?: number;
    minChars?: number;
    onSuccess?: (results: T | null, query: string) => void;
    onError?: (error: Error) => void;
    emptySearchCallback?: () => void | Promise<T>,
    skipFirstLoading?: boolean;
}

interface UseSearchBarReturn<T> {
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    results: T | null;
    error: string | null;
    clearSearch: () => void;
}

export const useSearchBar = <T = unknown>(
    searchCallback: (query: string) => Promise<T>,
    options: UseSearchBarOptions<T> = {}
): UseSearchBarReturn<T> => {
    const {
        debounceMs = 300,
        minChars = 0,
        onSuccess,
        onError,
        skipFirstLoading = false,
    } = options;

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [results, setResults] = useState<T | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [skipSearch, setSkipSearch] = useState<boolean>(skipFirstLoading);

    const performSearch = useCallback(async (query: string) => {
        setError(null);

        try {
            let data: T;

            if (query.trim() === '' && options.emptySearchCallback) {
                const result = options.emptySearchCallback();

                if (result instanceof Promise) {
                    data = await result;
                } else {
                    setResults(null);
                    return;
                }
            } else {
                data = await searchCallback(query);
            }

            setResults(data);
            onSuccess?.(data, query);
        } catch (err) {
            const errorMessage = err instanceof Error
                ? err.message
                : 'Wystąpił błąd podczas wyszukiwania';
            setError(errorMessage);
            setResults(null);

            if (err instanceof Error) {
                onError?.(err);
            }
        }
    }, []);

    useEffect(() => {
        if (skipSearch){
            setSkipSearch(false);
            return;
        }

        if (searchQuery.length < minChars) {
            setResults(null);
            setError(null);
            return;
        }

        const timeoutId = setTimeout(() => {
            performSearch(searchQuery);
        }, debounceMs);

        return () => clearTimeout(timeoutId);
    }, [searchQuery, debounceMs, minChars, performSearch]);

    const clearSearch = useCallback(() => {
        setSearchQuery('');
        setResults(null);
        setError(null);
        performSearch('');
    }, [performSearch]);

    return {
        searchQuery,
        setSearchQuery,
        results,
        error,
        clearSearch,
    };
};
